import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { useCreateProjectFromStartup } from "@/hooks/useCreateProjectFromStartup";
import { BuilderEntryState } from "@/components/code-workspace/BuilderEntryState";
import { BuilderBuildingState } from "@/components/code-workspace/BuilderBuildingState";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import posthog from "posthog-js";

const DEMO_PROMPT =
  "A SaaS analytics dashboard with user metrics, revenue charts, activity feed, and settings. Include a sidebar with navigation, stat cards on the dashboard, and a data table for recent transactions.";

export default function Create() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createProjectFromPromptWithPlanMode, isCreating, progress } = useCreateProjectFromStartup();
  const { canCreateProject } = useSubscription();
  const [logs, setLogs] = useState<string[]>([]);

  const [ideaPrompt, setIdeaPrompt] = useState<string | null>(null);

  // Fetch build prompt when ideaId is present
  const ideaId = searchParams.get("ideaId");
  const isValidUuid = ideaId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ideaId);
  useEffect(() => {
    if (!isValidUuid) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("ideas")
          .select("title, ai_prompts")
          .eq("id", ideaId)
          .eq("is_public", true)
          .maybeSingle();
        if (error || !data) {
          console.error("[Create] Failed to fetch idea:", error?.message);
          return;
        }
        const buildPrompt = (data.ai_prompts as any)?.build_app;
        if (buildPrompt) {
          setIdeaPrompt(buildPrompt);
        } else {
          // Fallback: use the idea title as a prompt seed
          setIdeaPrompt(`Build an app for: ${data.title}`);
        }
      } catch {
        console.error("[Create] Error fetching idea");
      }
    })();
  }, [ideaId]);

  const initialPrompt = useMemo(() => {
    if (searchParams.get("demo") === "1") return DEMO_PROMPT;
    // ideaId takes priority over other prefill methods
    if (ideaPrompt !== null) return ideaPrompt;
    const fromState = (location.state as { prefill?: string } | null)?.prefill;
    if (fromState?.trim()) return fromState.trim();
    const fromUrl = searchParams.get("prefill");
    if (fromUrl) {
      try {
        return decodeURIComponent(fromUrl);
      } catch {
        return fromUrl;
      }
    }
    return "";
  }, [searchParams, location.state, ideaPrompt]);

  useEffect(() => {
    if (isCreating && progress) {
      setLogs((prev) => (prev[prev.length - 1] === progress ? prev : [...prev, progress]));
    } else if (!isCreating) {
      setLogs([]);
    }
  }, [isCreating, progress]);

  // Auto-submit when arriving via ?demo=1
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (
      searchParams.get("demo") === "1" &&
      initialPrompt &&
      !isCreating &&
      !autoSubmittedRef.current
    ) {
      autoSubmittedRef.current = true;
      // Small delay so the UI renders before triggering the build
      const t = setTimeout(() => handleSubmit(initialPrompt), 300);
      return () => clearTimeout(t);
    }
  }, [initialPrompt, isCreating]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (prompt: string) => {
    // TODO: re-enable project limit check before public launch
    // BYPASSED for testing - no redirect to pricing
    // if (user?.id) {
    //   try {
    //     const { count } = await supabase
    //       .from("code_projects")
    //       .select("id", { count: "exact", head: true })
    //       .eq("user_id", user.id);
    //     if (count !== null && !canCreateProject(count)) {
    //       toast.error("You've reached the project limit on your plan. Upgrade to create more.");
    //       navigate("/pricing");
    //       return;
    //     }
    //   } catch {
    //     // Don't block creation if count check fails
    //   }
    // }

    posthog.capture("build_started", { source: ideaId ? "idea" : "prompt" });
    setLogs(["Creating project...", "Analyzing your idea..."]);
    await createProjectFromPromptWithPlanMode(prompt);
  };

  if (isCreating) {
    return (
      <>
        <SEOHead title="Building Your App | DayOne" description="AI is generating your application" />
        <BuilderBuildingState progress={progress} logs={logs.length ? logs : [progress]} />
      </>
    );
  }

  return (
    <>
      <SEOHead title="Create | DayOne" description="Build your app from a prompt" />
      <BuilderEntryState onSubmit={handleSubmit} isLoading={false} initialPrompt={initialPrompt} />
    </>
  );
}
